import { useEffect, useState } from 'react';

// How often to explicitly ask the browser to re-check sw.js. Browsers only do
// this on their own on a full navigation (rare in an SPA) plus an unreliable
// ~24h background timer — an idle PWA left open for a session can otherwise
// go a long time without noticing a deploy. reg.update() just byte-compares
// sw.js, cheap enough to poll.
const CHECK_INTERVAL_MS = 20 * 60 * 1000;

// Fires when the Service Worker transitions to a new version.
// With registerType:'autoUpdate', Workbox calls skipWaiting() automatically
// so controllerchange fires shortly after a new build is deployed.
// On iOS PWA this doesn't auto-reload — we need the user to tap.
export function useUpdateReady(): boolean {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    let reg: ServiceWorkerRegistration | undefined;

    navigator.serviceWorker.getRegistration().then((r) => {
      reg = r;
      // Already waiting SW? (page loaded while a new SW was sitting)
      if (reg?.waiting) setReady(true);
    });

    // New SW took control after skipWaiting()
    const onController = () => setReady(true);
    navigator.serviceWorker.addEventListener('controllerchange', onController);

    const checkForUpdate = () => { reg?.update().catch(() => {}); };
    const intervalId = window.setInterval(checkForUpdate, CHECK_INTERVAL_MS);

    // Also check whenever the PWA comes back to the foreground (phone was
    // locked / app was backgrounded) — the single most common "session
    // boundary" for a kid's app, and cheaper to catch than waiting for the
    // interval.
    const onVisible = () => {
      if (document.visibilityState === 'visible') checkForUpdate();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      navigator.serviceWorker.removeEventListener('controllerchange', onController);
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  return ready;
}
